import { supabase } from '../lib/supabase'
import {
  DESIGN_STUDIO_SLIDESHOW_SETTINGS_KEY,
  EMPTY_DESIGN_STUDIO_SLIDESHOW,
  normalizeDesignStudioSlideshowSettings,
  normalizeIntroVideo,
  type DesignStudioIntroVideo,
  type DesignStudioSlide,
  type DesignStudioSlideshowPlacement,
  type DesignStudioSlideshowSettings,
} from '../config/blissDesignStudioSlideshow'
import { photoPublicUrl } from './blissBasicCatalogAdminService'

const BUCKET = 'bliss-basic-gallery'

export type DesignStudioSlidePublic = {
  id: string
  url: string
  caption?: string
}

export type DesignStudioSlideshowPublic = {
  top: DesignStudioSlidePublic[]
  bottom: DesignStudioSlidePublic[]
  introVideo?: DesignStudioIntroVideo | null
}

export const DESIGN_STUDIO_SLIDESHOW_API = '/api/design-studio-slideshow'

export async function fetchDesignStudioSlideshowPublic(): Promise<DesignStudioSlideshowPublic> {
  try {
    const res = await fetch(DESIGN_STUDIO_SLIDESHOW_API)
    if (!res.ok) return { top: [], bottom: [] }
    const json = (await res.json()) as DesignStudioSlideshowPublic
    return {
      top: json.top ?? [],
      bottom: json.bottom ?? [],
      introVideo: json.introVideo === null ? null : normalizeIntroVideo(json.introVideo),
    }
  } catch {
    return { top: [], bottom: [] }
  }
}

function toPublic(slides: DesignStudioSlide[]): DesignStudioSlidePublic[] {
  return slides.map(s => ({
    id: s.id,
    url: photoPublicUrl(s.storagePath),
    caption: s.caption,
  }))
}

export function slideshowSettingsToPublic(
  settings: DesignStudioSlideshowSettings
): DesignStudioSlideshowPublic {
  return {
    top: toPublic(settings.top),
    bottom: toPublic(settings.bottom),
    introVideo: settings.introVideo === null ? null : normalizeIntroVideo(settings.introVideo),
  }
}

export function readSlideshowFromTenantSettings(
  settings: Record<string, unknown> | null | undefined
) {
  return normalizeDesignStudioSlideshowSettings(settings?.[DESIGN_STUDIO_SLIDESHOW_SETTINGS_KEY])
}

export async function uploadDesignStudioSlide(
  tenantId: string,
  placement: DesignStudioSlideshowPlacement,
  file: File,
  currentSettings: DesignStudioSlideshowSettings
): Promise<DesignStudioSlideshowSettings> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg'
  const safeExt = ['jpg', 'jpeg', 'png', 'webp'].includes(ext) ? ext : 'jpg'
  const objectPath = `${tenantId}/design-studio/${placement}/${crypto.randomUUID()}.${safeExt}`

  const { error: uploadError } = await supabase.storage.from(BUCKET).upload(objectPath, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type || 'image/png',
  })
  if (uploadError) throw uploadError

  const list = [...currentSettings[placement]]
  const slide: DesignStudioSlide = {
    id: crypto.randomUUID(),
    storagePath: objectPath,
    caption: file.name.replace(/\.[^.]+$/, ''),
    sortOrder: list.length,
  }
  list.push(slide)
  return { ...currentSettings, [placement]: list }
}

export async function removeDesignStudioSlide(
  settings: DesignStudioSlideshowSettings,
  placement: DesignStudioSlideshowPlacement,
  slideId: string
): Promise<DesignStudioSlideshowSettings> {
  const target = settings[placement].find(s => s.id === slideId)
  if (target) {
    await supabase.storage.from(BUCKET).remove([target.storagePath])
  }
  const next = settings[placement]
    .filter(s => s.id !== slideId)
    .map((s, i) => ({ ...s, sortOrder: i }))
  return { ...settings, [placement]: next }
}

export function emptySlideshowSettings(): DesignStudioSlideshowSettings {
  return structuredClone(EMPTY_DESIGN_STUDIO_SLIDESHOW)
}
